import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import GrantEmailAccess from 'src/containers/GrantEmailAccountAccess';
import axios from 'src/utils/axios';
import authService from 'src/services/authService';
import helpers from 'src/helpers';

// CON-1996 — email filter change notice. Shown once, on the first app load
// after sign-in or startup, to consultants who get their company emails by
// forwarding, when their company has new emails CP needs (the server decides
// who: GET /api/emailFilterNotices/current). Two choices: "Add the filter
// myself" (opens the notice's instructions page) and, for Gmail and
// Microsoft only, "Connect my email" (one tap with a working stored Gmail
// connection, else the Google / Microsoft sign-in). Closing it counts as
// dismissed. Never fetched during Sign In As, and the server also refuses it.
// Peer of the What's New / Share Set modals, mounted in DashboardLayout.

const STEP = {
  CHOOSE: 'choose',
  SIGN_IN: 'signIn',
  CONNECTED: 'connected',
};

const post = (noticeId, action, body = {}) => axios.post(
  `/api/emailFilterNotices/${noticeId}/${action}`,
  { surface: 'web', ...body },
);

function EmailFilterNoticeModal() {
  const email = useSelector((state) => state.account?.user?.email);

  const [notice, setNotice] = useState(null);
  const [step, setStep] = useState(STEP.CHOOSE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (authService.getSignInAsOriginal()) {
        return;
      }
      try {
        const response = await axios.get('/api/emailFilterNotices/current', { params: { surface: 'web' } });
        const current = response?.data?.notice;
        if (!cancelled && current?.id) {
          setNotice(current);
          post(current.id, 'shown').catch(() => { });
        }
      } catch (e) {
        // Fail open: no notice.
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const close = useCallback(() => {
    setNotice(null);
  }, []);

  const handleDismiss = useCallback(() => {
    if (notice?.id && step !== STEP.CONNECTED) {
      post(notice.id, 'choice', { choice: 'dismissed' }).catch(() => { });
    }
    close();
  }, [notice, step, close]);

  const handleInstructions = useCallback(() => {
    window.open(notice.instructionsUrl, '_blank', 'noopener');
    post(notice.id, 'choice', { choice: 'instructions' }).catch(() => { });
    close();
  }, [notice, close]);

  const connect = useCallback(async (authCode = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post(notice.id, 'connect', authCode ? { authCode } : {});
      const result = response?.data || {};

      if (result.success) {
        setStep(STEP.CONNECTED);
      } else if (result.needsOAuth) {
        setStep(STEP.SIGN_IN);
      } else {
        setError(result.screenMessage || 'We could not connect your email. Please try again.');
      }
    } catch (e) {
      setError(e?.screenMessage || e?.response?.data?.screenMessage
        || 'We could not connect your email. Please try again, or choose Add the filter myself.');
    } finally {
      setLoading(false);
    }
  }, [notice]);

  const handleAuthSuccess = useCallback((response) => {
    const authCode = response?.code || (helpers.isString(response) ? response : null);
    if (authCode) {
      connect(authCode);
    }
  }, [connect]);

  const handleAuthError = useCallback((authError) => {
    setError(authError?.errorMessage || 'We could not connect your email. Please try again.');
  }, []);

  if (!notice) {
    return null;
  }

  const errorText = error && (
    <Typography variant="body2" color="error" sx={{ mt: 2, wordBreak: 'break-word' }}>
      {error}
    </Typography>
  );

  const notNowLink = (
    <Button
      variant="text"
      onClick={step === STEP.CONNECTED ? close : handleDismiss}
      disabled={loading}
      sx={{ textTransform: 'none', alignSelf: 'center' }}
    >
      {step === STEP.CONNECTED ? 'Close' : 'Not now'}
    </Button>
  );

  // Buttons stacked at one full width, Connect first; "Not now" is a plain
  // link under them (CON-1996 simulator review).
  const choices = (
    <Box>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', mb: 2 }}>
        {notice.message}
      </Typography>
      {notice.pendingConnect && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          You chose to connect your email in the app. You can finish here.
        </Typography>
      )}
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch',
      }}
      >
        {notice.connectOffered && (
          <>
            <Button
              variant="contained"
              fullWidth
              onClick={() => connect()}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Connect my email
            </Button>
            <Typography variant="body2" color="textSecondary" align="center">
              {`CP only looks for your ${notice.companyName || 'company'} emails, the same ones your filters send us today. Nothing else in your inbox.`}
            </Typography>
          </>
        )}
        <Button
          variant={notice.connectOffered ? 'outlined' : 'contained'}
          fullWidth
          onClick={handleInstructions}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Add the filter myself
        </Button>
        {notNowLink}
      </Box>
      {errorText}
    </Box>
  );

  const signIn = (
    <Box>
      <GrantEmailAccess
        loading={loading}
        emailAddress={email || ''}
        emailProvider={notice.provider}
        calledFrom="EmailFilterNotice"
        onAuthSuccess={handleAuthSuccess}
        setBackEndError={handleAuthError}
        includeFilterManagementScope={notice.provider === 'Gmail'}
      />
      {errorText}
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2,
      }}
      >
        <Button onClick={handleInstructions} disabled={loading} sx={{ textTransform: 'none' }}>
          Add the filter myself instead
        </Button>
        {notNowLink}
      </Box>
    </Box>
  );

  const connected = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Your email is connected. We now read your company emails from your inbox, so you do not need to add the filter.
      </Typography>
      {notNowLink}
    </Box>
  );

  const contentByStep = {
    [STEP.CHOOSE]: choices,
    [STEP.SIGN_IN]: signIn,
    [STEP.CONNECTED]: connected,
  };

  return (
    <ModalDialogMUI6
      isVisible
      isHeaderVisible
      title={step === STEP.CONNECTED ? 'You are all set' : notice.title}
      content={contentByStep[step]}
      onClose={step === STEP.CONNECTED ? close : handleDismiss}
      buttonsListOrder={[]}
      modalSize="sm"
    />
  );
}

export default EmailFilterNoticeModal;
